import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
// import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'
import cogoToast from '@successtar/cogo-toast'
import { useConfigStore } from '@renderer/stores/configState'
// import Button from '@renderer/components/Button'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { Package } from '@renderer/types/configState'
// import { FaVideo } from 'react-icons/fa'
// import { IoPrint } from 'react-icons/io5'
// import { MdOutlineGifBox, MdOutlineVideoLibrary } from 'react-icons/md'
import { DeepPartial, SessionStateData, ActivityLog } from '@renderer/types/sessionState'
import { useTranslation } from 'react-i18next'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'
import { getNextPageAfterPaymentFlow } from '@renderer/utils/navigationUtils'
// import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'

const CheckoutPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, transaction, userPreferences, mode } = useSessionStateStore()
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const config = globalConfig?.pages.checkout
  const paymentConfig = globalConfig?.pages.payment
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  const welcomeSettings = getPageSettings('welcome')
  // const [showPromoInput, setShowPromoInput] = useState(false)
  // const [promoCode, setPromoCode] = useState('')
  const [remainingTime, setRemainingTime] = useState<string>('')
  const [numberOfPeople, setNumberOfPeople] = useState<number>(1)
  // const { type: subscriptionType } = useSubscriptionStore()

  // Memoize packages calculations
  const packages = useMemo(() => config?.settings.packages || [], [config])
  const printPackages = useMemo(
    () =>
      mode === 'capture'
        ? packages
            .filter((pkg) => pkg.type === 'print')
            .sort((a, b) => (a.quantity || 0) - (b.quantity || 0))
        : [],
    [mode, packages]
  )
  const gifPackage = useMemo(
    () => (mode === 'capture' ? packages.find((pkg) => pkg.type === 'gif') : null),
    [mode, packages]
  )
  const recordingPackage = useMemo(
    () =>
      mode === 'capture'
        ? packages.find(
            (pkg) =>
              pkg.type === 'recording' ||
              pkg.type === 'strip_live' ||
              pkg.type === 'strip_story' ||
              pkg.type === 'session_recording'
          )
        : null,
    [mode, packages]
  )
  const videoPackage = useMemo(
    () => (mode === 'record' ? packages.find((pkg) => pkg.type === 'video') : null),
    [mode, packages]
  )

  // Get recording type for display
  const recordingType = useMemo(() => {
    if (!recordingPackage) return null
    // Default to live mode for backward compatibility
    if (recordingPackage.type === 'recording') return 'strip_live' as const
    return recordingPackage.type as 'strip_live' | 'strip_story' | 'session_recording'
  }, [recordingPackage])

  // Initial navigation checks — nothing purchasable on this Checkout, so skip straight
  // through. With startWithPayment this is the pre-capture leg, so it must resume the
  // filter/frame flow rather than jumping to capture and skipping both steps.
  useEffect(() => {
    if (mode === 'capture' && !printPackages.length && !gifPackage && !recordingPackage) {
      navigate(
        `/${projectId}/${getNextPageAfterPaymentFlow(globalConfig, {
          startWithPayment: welcomeSettings?.startWithPayment || false,
          mode
        })}`
      )
    }
    if (mode === 'record' && !videoPackage) {
      navigate(`/${projectId}/record`)
    }
  }, [
    mode,
    printPackages,
    gifPackage,
    recordingPackage,
    videoPackage,
    navigate,
    projectId,
    globalConfig,
    welcomeSettings
  ])

  // Initialize selected print quantity and session state
  const [selectedPrintQuantity, setSelectedPrintQuantity] = useState<number>(
    printPackages[0]?.quantity || 1
  )

  // Initial session state setup
  useEffect(() => {
    if (config?.settings.packages?.length && !transaction.selectedPackages.length) {
      // Use a microtask to avoid render-phase updates
      Promise.resolve().then(() => {
        setSessionState({
          userPreferences: {
            copies: selectedPrintQuantity,
            useGIF: gifPackage ? userPreferences.useGIF : false,
            useRecording: recordingPackage ? userPreferences.useRecording : false,
            useVideo: videoPackage ? userPreferences.useVideo : false,
            recordingType: recordingType as
              | 'recording'
              | 'strip_live'
              | 'strip_story'
              | 'session_recording'
              | null
              | undefined
          }
        })
      })
    }
  }, [
    config,
    packages,
    selectedPrintQuantity,
    gifPackage,
    recordingPackage,
    videoPackage,
    recordingType,
    numberOfPeople
  ])

  const getSelectedPrintPackage = (): Package => {
    return printPackages.find((pkg) => pkg.quantity === selectedPrintQuantity) || printPackages[0]
  }

  // Calculate person price
  const calculatePersonPrice = (): number => {
    if (numberOfPeople >= 1 && numberOfPeople <= 3) {
      return 0
    }
    if (numberOfPeople > 3) {
      return (numberOfPeople - 3) * 15000
    }
    return 0
  }

  // Calculate subtotal (before discount)
  const calculateSubtotal = (): number => {
    let subtotal = 0
    if (paymentConfig?.settings.enabled) {
      if (mode === 'capture') {
        const selectedPrintPackage = getSelectedPrintPackage()
        subtotal += Math.round(selectedPrintPackage?.price || 0)
        if (gifPackage && userPreferences.useGIF) subtotal += Math.round(gifPackage.price)
        if (recordingPackage && userPreferences.useRecording)
          subtotal += Math.round(recordingPackage.price)
      } else if (mode === 'record' && videoPackage && userPreferences.useVideo) {
        subtotal += Math.round(videoPackage.price)
      }
    }
    subtotal += calculatePersonPrice() // Add person price to subtotal
    return Math.round(subtotal)
  }

  // Calculate total price (after discount)
  const calculateTotal = (): number => {
    const subtotal = calculateSubtotal()
    const discount = Math.round(transaction.discount || 0)
    return Math.round(Math.max(0, subtotal - discount))
  }

  // Initialize countdown with the useCountdown hook
  const checkoutTimeout = config?.settings?.checkoutTimeout || 0
  const countdown = useCountdown(checkoutTimeout)

  // Set up countdown callbacks
  useEffect(() => {
    if (checkoutTimeout > 0) {
      // Set the onTick callback to update the remaining time display
      countdown.setOnTick(() => {
        setRemainingTime(countdown.getFormattedRemainingSeconds())
      })

      // Set the onComplete callback
      countdown.setOnComplete(() => {
        setSessionState({
          timeline: {
            activityLogs: [
              {
                page: 'checkout',
                type: 'selection',
                action: 'timeout',
                details: {
                  selectedPackages: transaction.selectedPackages?.map((pkg) => pkg.type),
                  amount: calculateTotal(),
                  timeoutDuration: checkoutTimeout
                },
                timestamp: new Date()
              }
            ]
          }
        })
        handleNext()
      })
    }
  }, [checkoutTimeout, transaction.selectedPackages, setSessionState])

  // Start the countdown on mount
  useEffect(() => {
    if (checkoutTimeout > 0) {
      countdown.start()

      return (): void => {
        countdown.stop()
      }
    }
    return
  }, [checkoutTimeout])

  // const handlePromoKeyPress = (key: string): void => {
  //   if (promoCode.length < 10) {
  //     setPromoCode(promoCode + key)
  //   }
  // }

  // const handlePromoBackspace = (): void => {
  //   setPromoCode(promoCode.slice(0, -1))
  // }

  // const handlePromoEnter = async (code: string): Promise<void> => {
  //   try {
  //     const timestamp = new Date()

  //     // Check subscription type
  //     // if (
  //     //   subscriptionType === 'free' ||
  //     //   subscriptionType === 'starter' ||
  //     //   subscriptionType === null
  //     // ) {
  //     //   const activityLog: ActivityLog = {
  //     //     page: 'checkout',
  //     //     type: 'interaction',
  //     //     action: 'failure',
  //     //     element: 'promo-code',
  //     //     details: {
  //     //       code,
  //     //       message: "Promo isn't available for this device"
  //     //     },
  //     //     timestamp
  //     //   }

  //     //   setSessionState({
  //     //     timeline: {
  //     //       activityLogs: [activityLog]
  //     //     }
  //     //   })

  //     //   cogoToast.error("Promo isn't available for this device")
  //     //   return
  //     // }

  //     const response = await fetch(`${process.env.VITE_API_URL}/api/promos/apply`, {
  //       method: 'POST',
  //       credentials: 'include',
  //       headers: {
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify({
  //         code,
  //         projectId,
  //         amount: Math.round(calculateSubtotal())
  //       })
  //     })

  //     const data = await response.json()
  //     const isValid = response.ok && data.isValid

  //     const activityLog: ActivityLog = {
  //       page: 'checkout',
  //       type: 'interaction',
  //       action: isValid ? 'success' : 'failure',
  //       element: 'promo-code',
  //       details: {
  //         code,
  //         message: data.message,
  //         isCash: data.isCash
  //       },
  //       timestamp
  //     }

  //     const updates: DeepPartial<SessionStateData> = {
  //       transaction: {
  //         amount: isValid
  //           ? Math.round(calculateSubtotal() - (Math.round(data.discount) || 0))
  //           : undefined,
  //         discount: isValid ? Math.round(data.discount || 0) : undefined,
  //         promoCode: isValid ? code : undefined,
  //         // If it's a cash payment promo, set status to settlement immediately
  //         status: isValid && data.isCash ? 'settlement' : undefined
  //       },
  //       timeline: {
  //         activityLogs: [activityLog]
  //       }
  //     }

  //     setSessionState(updates)

  //     if (isValid) {
  //       cogoToast.success(t('checkout.promoApplied', 'Promo code applied successfully!'))
  //       setShowPromoInput(false)
  //     } else {
  //       cogoToast.error(data.message || t('checkout.invalidPromo', 'Invalid promo code'))
  //     }
  //   } catch (error) {
  //     console.error('Error validating promo code:', error)
  //     cogoToast.error(
  //       t('checkout.promoValidationFailed', 'Failed to validate promo code. Please try again.')
  //     )
  //   }
  // }

  const handleNext = async (): Promise<void> => {
    if (!transaction.selectedPackages) return

    if (numberOfPeople === 0) {
      cogoToast.error(t('checkout.addNumberOfPeople', 'Tolong pilih jumlah orang.'))
      return
    }

    // Set user preferences based on selected quantities and toggles
    setSessionState({
      transaction: {
        amount: calculateTotal()
      },
      timeline: {
        activityLogs: [
          {
            page: 'checkout',
            type: 'selection',
            action: 'complete',
            details: {
              copies: selectedPrintQuantity,
              amount: calculateTotal(),
              package: transaction.selectedPackages.map((pkg) => pkg.type).join(', '),
              packageDescription: transaction.selectedPackages
                .map((pkg) => pkg.description)
                .join(', '),
              promoApplied: false,
              promoCode: undefined
            },
            timestamp: new Date()
          }
        ]
      }
    })

    // If transaction status is already settlement (cash payment), skip payment page
    if (transaction.status === 'settlement') {
      const nextPage = getNextPageAfterPaymentFlow(globalConfig, {
        startWithPayment: welcomeSettings?.startWithPayment || false,
        mode
      })
      navigate(`/${projectId}/${nextPage}`)
      return
    }

    // Navigate based on startWithPayment / applyAfterCapture and mode
    const nextPage = paymentConfig?.settings.enabled
      ? calculateTotal() === 0
        ? getNextPageAfterPaymentFlow(globalConfig, {
            startWithPayment: welcomeSettings?.startWithPayment || false,
            mode
          })
        : 'payment'
      : getNextPageAfterPaymentFlow(globalConfig, {
          startWithPayment: welcomeSettings?.startWithPayment || false,
          mode
        })
    navigate(`/${projectId}/${nextPage}`)
  }

  // Once a voucher/promo has fully settled the transaction (cash voucher or
  // 100% discount), the paid amount and package selection must stay frozen.
  // Otherwise the customer can add/increase items (e.g. print copies) for
  // free after the session is already marked as paid, since there is no
  // further payment step to collect the difference. Production evidence:
  // sessions ub2y47 (amount inflated past the voucher cap) and f27g86
  // (copies incremented past what the voucher covered).
  const isOrderLockedByVoucher = (): boolean => {
    if (transaction.status === 'settlement') {
      cogoToast.warn(
        t(
          'checkout.orderLockedByVoucher',
          'Remove the applied voucher to change your order.'
        )
      )
      return true
    }
    return false
  }

  const handlePackageChange = (
    packageType: string,
    value: boolean | number,
    packageInfo: Package
  ): void => {
    if (isOrderLockedByVoucher()) return

    const timestamp = new Date()
    const activityLog: ActivityLog = {
      page: 'checkout',
      type: 'selection',
      action: 'change',
      element: packageInfo.description || packageType,
      details: {
        packageType,
        value,
        price: packageInfo.price
      },
      timestamp
    }

    const updates: DeepPartial<SessionStateData> = {
      userPreferences: {
        [packageType === 'print'
          ? 'copies'
          : packageType === 'gif'
            ? 'useGIF'
            : `use${packageType.charAt(0).toUpperCase() + packageType.slice(1)}`]: value
      },
      transaction: {
        selectedPackages: value
          ? [...(transaction?.selectedPackages || []), packageInfo]
          : (transaction?.selectedPackages || []).filter((pkg) => pkg.type !== packageType),
        amount: calculateTotal()
      },
      timeline: {
        activityLogs: [activityLog]
      }
    }

    setSessionState(updates)
  }

  // Call all hooks at the top level
  const cachedCheckoutBg = useCachedBackgroundImage('/assets/photophoria/photophoria-checkout.webp')
  const cachedCheckoutButton = useCachedImage(
    '/assets/photophoria/photophoria-checkout-button.webp'
  )

  return (
    <div
      className="w-full h-full relative bg-cover bg-center"
      data-page="checkout"
      style={{
        backgroundImage: `url(${cachedCheckoutBg})`
      }}
    >
      <div className="absolute top-[310px] right-[420px] flex flex-col items-end gap-20 w-[400px]">
        {/* Number of People Adjustment */}
        <div className="flex items-center justify-end w-full gap-12">
          <div className="flex items-center rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => setNumberOfPeople((prev) => Math.max(1, prev - 1))}
              className="w-12 h-12 flex items-center justify-center text-4xl text-pink-500 font-serif-bahela bg-pink-200 rounded-full"
            >
              -
            </button>
            <span className="px-4 text-4xl font-bold w-20 h-12 flex items-center justify-center text-pink-500 font-serif-bahela">
              {numberOfPeople}
            </span>
            <button
              type="button"
              onClick={() => setNumberOfPeople((prev) => prev + 1)}
              className="w-12 h-12 flex items-center justify-center text-4xl text-pink-500 font-serif-bahela bg-pink-200 rounded-full"
            >
              +
            </button>
          </div>
          <span className="text-4xl text-right text-pink-500 font-serif-bahela w-40">
            {calculatePersonPrice() > 0
              ? formatAmount(calculatePersonPrice(), projectCurrency)
              : 'Free'}
          </span>
        </div>

        {/* Print Package Adjustment */}
        {mode === 'capture' && printPackages.length > 0 && (
          <div className="flex items-center justify-end w-full gap-12">
            <div className="flex items-center rounded-md overflow-hidden">
              <button
                type="button"
                title={t('checkout.minus', 'Minus')}
                className="w-12 h-12 flex items-center justify-center text-4xl text-pink-500 font-serif-bahela bg-pink-200 rounded-full"
                onClick={() => {
                  if (isOrderLockedByVoucher()) return
                  const newQuantity = Math.max(1, selectedPrintQuantity - 1)
                  const newPackage =
                    printPackages.find((pkg) => pkg.quantity === newQuantity) || printPackages[0]
                  handlePackageChange('print', newQuantity, newPackage)
                  setSelectedPrintQuantity(newQuantity)
                }}
              >
                -
              </button>
              <span className="px-4 text-4xl font-bold w-20 h-12 flex items-center justify-center text-pink-500 font-serif-bahela">
                {selectedPrintQuantity}
              </span>
              <button
                type="button"
                title={t('checkout.plus', 'Plus')}
                className="w-12 h-12 flex items-center justify-center text-4xl text-pink-500 font-serif-bahela bg-pink-200 rounded-full"
                onClick={() => {
                  if (isOrderLockedByVoucher()) return
                  const maxQuantity = printPackages[printPackages.length - 1].quantity
                  const newQuantity = Math.min(maxQuantity, selectedPrintQuantity + 1)
                  const newPackage =
                    printPackages.find((pkg) => pkg.quantity === newQuantity) || printPackages[0]
                  handlePackageChange('print', newQuantity, newPackage)
                  setSelectedPrintQuantity(newQuantity)
                }}
              >
                +
              </button>
            </div>
            <span className="text-4xl text-right text-pink-500 font-serif-bahela w-40">
              {getSelectedPrintPackage()?.price > 0
                ? formatAmount(getSelectedPrintPackage()?.price ?? 0, projectCurrency)
                : 'Free'}
            </span>
          </div>
        )}

        {/* GIF Package */}
        {mode === 'capture' && gifPackage && (
          <div className="flex items-center justify-end w-full gap-28">
            <input
              title={t('checkout.gif', 'GIF')}
              type="checkbox"
              className="w-8 h-8 cursor-pointer accent-pink-500"
              checked={userPreferences.useGIF}
              onChange={() =>
                gifPackage && handlePackageChange('gif', !userPreferences.useGIF, gifPackage)
              }
            />
            <span
              className={`text-4xl text-right text-pink-500 font-serif-bahela w-40 ${!userPreferences.useGIF && gifPackage.optional ? 'opacity-50' : ''}`}
            >
              {gifPackage.price > 0 ? formatAmount(gifPackage.price, projectCurrency) : 'Free'}
            </span>
          </div>
        )}

        {/* Recording Package */}
        {mode === 'capture' && recordingPackage && (
          <div className="flex items-center justify-end w-full gap-28">
            <input
              title={t('recording.title', 'Recording')}
              type="checkbox"
              className="w-8 h-8 cursor-pointer accent-pink-500"
              checked={userPreferences.useRecording}
              onChange={() =>
                recordingPackage &&
                handlePackageChange('recording', !userPreferences.useRecording, recordingPackage)
              }
            />
            <span
              className={`text-4xl text-right text-pink-500 font-serif-bahela w-40 ${!userPreferences.useRecording && recordingPackage.optional ? 'opacity-50' : ''}`}
            >
              {recordingPackage.price > 0
                ? formatAmount(recordingPackage.price, projectCurrency)
                : 'Free'}
            </span>
          </div>
        )}

        {/* Video Package (for 'record' mode) */}
        {mode === 'record' && videoPackage && (
          <div className="flex items-center justify-end w-full">
            <span className="text-4xl text-right text-pink-500 font-serif-bahela w-full">
              {videoPackage.price > 0 ? formatAmount(videoPackage.price, projectCurrency) : 'Free'}
            </span>
          </div>
        )}

        {/* Total Price */}
        <span className="text-5xl font-bold text-pink-500 font-serif-bahela text-right">
          {formatAmount(calculateTotal(), projectCurrency)}
        </span>
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-full flex flex-col items-center">
        <button onClick={handleNext} className="transition-transform transform hover:scale-105">
          <img
            src={cachedCheckoutButton}
            alt={t('common.continue', 'Continue')}
            className="w-72 h-auto"
          />
        </button>
        <div className="text-lg text-pink-500 font-serif-bahela text-center">
          {remainingTime !== '' && remainingTime}
        </div>
      </div>
    </div>
  )
}

export default CheckoutPage
